import * as _ from "lodash";
import * as $ from "jquery";
import {BaseView} from "../_BaseViews";
import {DashboardTab} from "../../models/DashboardTab";
import {PermissionCollection} from "../../collections/Permissions";

declare var App;

export interface DashboardTabViewOptions extends Backbone.ViewOptions<Backbone.Model> {
    tab: DashboardTab;
    activeTabId: number;
    totalTabCount: number;
    permissionCollection: PermissionCollection;
    dashboardId: number;
    onTabClick: (tabId: number) => void;
    onTabEdit: (tab: DashboardTab) => void;
    onTabClone: (tab: DashboardTab) => void;
    onTabMove: (tab: DashboardTab) => void;
    onTabDelete: (tab: DashboardTab) => void;
}

export class DashboardTabView extends BaseView {
    options: DashboardTabViewOptions;
    private template = _.template(require('templates/dashboard/dashboardTabs.html'));
    private isDragging: boolean = false;
    private dragEnterCount: number = 0;

    constructor(options: DashboardTabViewOptions) {
        super(_.extend({
            tagName: "div",
            className: "dashboard-tab",
            events: {
                'click': 'handleClick',
                'click .tab-menu-toggle': 'handleMenuToggle',
                'click .tab-edit': 'handleEdit',
                'click .tab-clone': 'handleClone',
                'click .tab-move': 'handleMove',
                'click .tab-delete': 'handleDelete',
                'click .dropdown-menu a': 'handleMenuClick',
                'dragstart': 'handleDragStart',
                'dragend': 'handleDragEnd',
                'dragover': 'handleDragOver',
                'drop': 'handleDrop',
                'dragenter': 'handleDragEnter',
                'dragleave': 'handleDragLeave'
            }
        }, options));
    }

    render() {
        const tab = this.options.tab;
        const isActive = tab.get('id') === this.options.activeTabId;
        const totalTabCount = this.options.totalTabCount;
        const canModify = this.options.permissionCollection.canModifyDashboard(this.options.dashboardId);
        const canDelete = this.options.permissionCollection.canDeleteDashboard(this.options.dashboardId);
        const showMenu = canModify || canDelete;
        
        this.$el.html(this.template({
            tab: tab,
            isActive: isActive,
            totalTabCount: totalTabCount,
            showMenu: showMenu
        }));
        
        this.$el.attr('data-tab-id', tab.get('id'));
        
        if (isActive) {
            this.$el.addClass('active');
        } else {
            this.$el.removeClass('active');
        }
        
        // Initialize dropdown menu - close all other dropdowns when this one opens
        const $toggle = this.$('.tab-menu-toggle');
        if ($toggle.length) {
            const $menu = this.$('.dropdown-menu');
            const self = this;
            
            // Toggle dropdown on click
            $toggle.off('click.dropdown').on('click.dropdown', (e) => {
                e.preventDefault();
                e.stopPropagation();
                const $existingBodyMenu = $('body > .tailwind-scope .tab-dropdown-menu');
                const isOpen = $existingBodyMenu.length > 0 && $existingBodyMenu.data('tab-id') === self.options.tab.get('id');
                
                // Close all other dropdowns
                $('.dashboard-tab .dropdown-menu').hide();
                $('body > .tailwind-scope:has(.tab-dropdown-menu)').remove(); // Remove any orphaned dropdowns
                
                if (!isOpen) {
                    // Get position of toggle button (viewport-relative for position: fixed)
                    const rect = $toggle[0].getBoundingClientRect();
                    const menuWidth = 120; // Fixed width matching tw-min-w-[120px] from template
                    
                    // Create wrapper with tailwind-scope and clone menu inside it
                    const $wrapper = $('<div class="tailwind-scope"></div>').appendTo('body');
                    const $bodyMenu = $menu.clone().addClass('tab-dropdown-menu').appendTo($wrapper);
                    $bodyMenu.data('tab-id', self.options.tab.get('id'));
                    // Position wrapper aligned to the right edge of the toggle button
                    $wrapper.css({
                        position: 'fixed',
                        top: rect.bottom + 'px',
                        left: (rect.right - menuWidth) + 'px', // Align right edge with toggle button
                        width: menuWidth + 'px',
                        zIndex: 9999
                    });
                    $bodyMenu.css({
                        display: 'block'
                    });

                    // Close dropdown on horizontal scroll (tabs container) - menu would stay orphaned
                    const $tabsContainer = self.$el.closest('.dashboard-tabs');
                    const closeDropdown = () => {
                        $wrapper.remove();
                        $tabsContainer.off('scroll.tabDropdown');
                    };
                    $tabsContainer.on('scroll.tabDropdown', closeDropdown);
                    
                    // Close dropdown when clicking outside - use one() so it only fires once
                    setTimeout(() => {
                        $(document).one('click', function(docE) {
                            const $clickedEl = $(docE.target || docE.currentTarget || document);
                            if (!$clickedEl.closest($toggle).length && !$clickedEl.closest($wrapper).length) {
                                closeDropdown();
                            }
                        });
                    }, 0);
                    
                    // Handle menu item clicks - trigger the original handlers
                    $bodyMenu.find('.tab-edit').on('click', function(e) {
                        e.preventDefault();
                        closeDropdown();
                        self.handleEdit(e);
                    });
                    $bodyMenu.find('.tab-clone').on('click', function(e) {
                        e.preventDefault();
                        closeDropdown();
                        self.handleClone(e);
                    });
                    $bodyMenu.find('.tab-move').on('click', function(e) {
                        e.preventDefault();
                        closeDropdown();
                        self.handleMove(e);
                    });
                    $bodyMenu.find('.tab-delete').on('click', function(e) {
                        e.preventDefault();
                        closeDropdown();
                        self.handleDelete(e);
                    });
                } else {
                    $menu.hide();
                    $('body > .tailwind-scope:has(.tab-dropdown-menu)').remove();
                }
            });
        }
        
        return this;
    }

    handleClick(e: any) {
        e.preventDefault();
        // Don't trigger click if we just finished dragging
        if (this.isDragging) {
            this.isDragging = false;
            return;
        }
        // Check if click was on menu toggle, edit, clone, move, or delete - those have their own handlers
        const $target = $(e.currentTarget);
        if ($target.hasClass('tab-menu-toggle') || $target.closest('.tab-menu-toggle').length ||
            $target.hasClass('tab-edit') || $target.closest('.tab-edit').length ||
            $target.hasClass('tab-clone') || $target.closest('.tab-clone').length ||
            $target.hasClass('tab-move') || $target.closest('.tab-move').length ||
            $target.hasClass('tab-delete') || $target.closest('.tab-delete').length ||
            $target.closest('.dropdown-menu').length) {
            return; // Let menu handlers handle it
        }
        this.options.onTabClick(this.options.tab.get('id'));
    }
    
    handleMenuToggle(e: JQuery.Event) {
        e.preventDefault();
        e.stopPropagation();
        // Dropdown toggle is handled in render() via direct event binding
    }
    
    handleMenuClick(e: JQuery.Event) {
        e.stopPropagation();
        // Prevent tab click when clicking menu items
    }

    handleEdit(e: JQuery.Event) {
        e.preventDefault();
        e.stopPropagation();
        this.options.onTabEdit(this.options.tab);
    }

    handleClone(e: JQuery.Event) {
        e.preventDefault();
        e.stopPropagation();
        // Close dropdown before cloning
        this.$('.dropdown-menu').hide();
        this.options.onTabClone(this.options.tab);
    }

    handleMove(e: JQuery.Event) {
        e.preventDefault();
        e.stopPropagation();
        // Close dropdown before moving
        this.$('.dropdown-menu').hide();
        this.options.onTabMove(this.options.tab);
    }

    handleDelete(e: JQuery.Event) {
        e.preventDefault();
        e.stopPropagation();
        // Close dropdown before deleting
        this.$('.dropdown-menu').hide();
        this.options.onTabDelete(this.options.tab);
    }

    handleDragStart(e: JQuery.DragStartEvent) {
        // Don't drag if clicking on menu icon or dropdown
        const dragEvent = e.originalEvent as DragEvent;
        if (dragEvent && dragEvent.target) {
            const $target = $(dragEvent.target as HTMLElement);
            if ($target.closest('.tab-menu-toggle, .dropdown-menu').length || 
                $target.hasClass('tab-menu-toggle')) {
                e.preventDefault();
                return false;
            }
        }
        
        this.isDragging = true;
        if (dragEvent && dragEvent.dataTransfer) {
            dragEvent.dataTransfer.effectAllowed = 'move';
            const tabId = this.options.tab.get('id').toString();
            dragEvent.dataTransfer.setData('text/plain', tabId);
            
            // Store source tab ID on the element for easier access during dragenter
            this.$el.attr('data-dragging-tab-id', tabId);
            
            // Add dragging class for CSS styling (opacity defined in template)
            this.$el.addClass('dragging');
            
            if (dragEvent.dataTransfer.setDragImage) {
                // Clone the element exactly as it is
                const dragImage = this.$el.clone(true);
                
                // Hide dropdown menu
                dragImage.find('.dropdown-menu').hide();
                dragImage.removeClass('dragging');
                
                // Force flex layout on the inner container with inline styles
                const flexContainer = dragImage.find('.tw-flex').first();
                if (flexContainer.length) {
                    flexContainer.css({
                        display: 'flex',
                        alignItems: 'center',
                        flexDirection: 'row',
                        gap: '0.5rem',
                        whiteSpace: 'nowrap'
                    });
                }
                
                // Get the original width before positioning off-screen
                const originalWidth = this.$el.width() || this.$el[0].offsetWidth;
                
                // Position off-screen but keep all original styles
                dragImage.css({
                    position: 'fixed',
                    top: '0',
                    left: '0',
                    pointerEvents: 'none',
                    opacity: '0.85',
                    visibility: 'visible',
                    width: originalWidth + 'px',
                    minWidth: originalWidth + 'px'
                });
                
                $('body').append(dragImage);
                
                // Force multiple reflows to ensure layout is computed
                dragImage[0].offsetHeight;
                dragImage[0].getBoundingClientRect();
                
                // Move off-screen after rendering
                dragImage.css({
                    top: '-10000px',
                    left: '-10000px'
                });
                
                // Calculate offset
                const rect = this.$el[0].getBoundingClientRect();
                const offsetX = dragEvent.clientX - rect.left;
                const offsetY = dragEvent.clientY - rect.top;
                
                dragEvent.dataTransfer.setDragImage(dragImage[0] as Element, offsetX, offsetY);
                
                setTimeout(() => dragImage.remove(), 0);
            }
        }
    }

    handleDragEnd(e: JQuery.DragEndEvent) {
        // Remove dragging class and data attribute
        this.$el.removeClass('dragging');
        this.$el.removeAttr('data-dragging-tab-id');
        
        // Remove drag-over from all tabs
        $('.dashboard-tab').removeClass('drag-over');
        
        // Reset dragging flag after a short delay to prevent click event
        const self = this;
        setTimeout(() => {
            self.isDragging = false;
        }, 100);
    }

    handleDragOver(e: JQuery.DragOverEvent) {
        e.preventDefault();
        e.stopPropagation();
        const dragEvent = e.originalEvent as DragEvent;
        if (dragEvent && dragEvent.dataTransfer) {
            dragEvent.dataTransfer.dropEffect = 'move';
        }
        return false;
    }

    handleDragEnter(e: JQuery.DragEnterEvent) {
        e.preventDefault();
        e.stopPropagation();
        
        // Increment counter to track nested enters (child elements)
        this.dragEnterCount++;
        
        // Find the source tab that's being dragged
        const $draggingTab = $('.dashboard-tab.dragging');
        if ($draggingTab.length === 0) {
            return false;
        }
        
        const sourceTabId = $draggingTab.attr('data-dragging-tab-id');
        const currentTabId = this.options.tab.get('id').toString();
        
        // Only show feedback if we're not dragging this tab itself
        if (sourceTabId && sourceTabId !== currentTabId) {
            this.$el.addClass('drag-over');
        }
        
        return false;
    }

    handleDragLeave(e: JQuery.DragLeaveEvent) {
        e.preventDefault();
        e.stopPropagation();
        
        // Decrement counter
        this.dragEnterCount--;
        
        // Only remove class when we've actually left the tab (counter reaches 0)
        // This prevents flickering when moving between child elements
        if (this.dragEnterCount <= 0) {
            this.dragEnterCount = 0;
            this.$el.removeClass('drag-over');
        }
        
        return false;
    }

    handleDrop(e: JQuery.DropEvent) {
        e.preventDefault();
        e.stopPropagation();
        
        // Reset counter
        this.dragEnterCount = 0;
        
        const dragEvent = e.originalEvent as DragEvent;
        if (!dragEvent || !dragEvent.dataTransfer) {
            return false;
        }

        const sourceTabId = parseInt(dragEvent.dataTransfer.getData('text/plain'), 10);
        if (isNaN(sourceTabId)) {
            return false;
        }

        const targetTabId = this.options.tab.get('id');
        if (sourceTabId === targetTabId) {
            this.$el.removeClass('drag-over');
            return false;
        }

        // Remove visual feedback
        this.$el.removeClass('drag-over');

        // Trigger a custom event that the parent view can listen to
        this.$el.trigger('tab:reorder', {
            sourceTabId: sourceTabId,
            targetTabId: targetTabId
        });

        return false;
    }
}




// WEBPACK FOOTER //
// ./srv/public/js/views/dashboard/DashboardTabView.ts